// Sizing & fit slide-in drawer.
//
// Triggers: any element with [data-sizing-drawer-trigger].
// Closers : any element with [data-sizing-drawer-close], the ESC key,
//           or clicking the dimming overlay.
//
// On open we portal the drawer node to <body> — the PDP info column has
// `position: sticky` + scroll, which would clip the drawer if we left it
// in place. Portaling is one append, no reparenting on close.
//
// Focus management: trap Tab inside the drawer while open; restore focus
// to the trigger on close. Required for a11y-compliant modals.

const STATE_OPEN  = 'open';
const STATE_CLOSE = 'closed';

const FOCUSABLE_SELECTOR = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

// Portal the inline size-guide trigger (rendered as a fallback inside
// the info column) to sit directly beneath the SIZE attribute row inside
// WC's variation-selector block. Uses a MutationObserver because the WC
// Interactivity API may re-render variation rows after hydration —
// re-portal if the trigger gets detached.
function portalSizeGuideTrigger() {
	const trigger = document.querySelector('[data-pending-portal="size-guide"]');
	if (!trigger) return;

	const placeTrigger = () => {
		// Find the SIZE attribute group via its <label> text. Walk up to
		// the `.wp-block-woocommerce-add-to-cart-with-options-variation-selector-attribute`
		// container so we drop the trigger as a sibling, not inside.
		// Match "size" anywhere in the label (not just at the start) —
		// sneaker products combine gender + size in one attribute named
		// e.g. "MEN SIZE(UK)" or "WOMEN SIZE(UK)", so leading-anchor
		// matching misses them.
		const labels = document.querySelectorAll(
			'.wp-block-woocommerce-add-to-cart-with-options-variation-selector-attribute-name'
		);
		const sizeLabel = [...labels].find((el) => /\bsize\b/i.test(el.textContent.trim()));
		if (!sizeLabel) return false;
		const sizeGroup = sizeLabel.closest(
			'.wp-block-woocommerce-add-to-cart-with-options-variation-selector-attribute'
		);
		if (!sizeGroup) return false;

		// Already in the right place?
		if (trigger.previousElementSibling === sizeGroup) {
			trigger.dataset.portaled = 'true';
			return true;
		}

		sizeGroup.parentNode.insertBefore(trigger, sizeGroup.nextSibling);
		trigger.dataset.portaled = 'true';
		return true;
	};

	if (placeTrigger()) {
		// Watch for re-renders. If WC swaps the attribute container, our
		// trigger will end up either detached or in a stale position —
		// re-portal idempotently.
		const root = document.querySelector('.wp-block-add-to-cart-with-options, .wc-block-add-to-cart-with-options');
		if (root) {
			new MutationObserver(() => {
				if (!trigger.isConnected || trigger.dataset.portaled !== 'true') {
					trigger.dataset.portaled = '';
					placeTrigger();
				}
			}).observe(root, { childList: true, subtree: true });
		}
		return;
	}

	// First attempt failed (WC not yet rendered). Retry briefly.
	let tries = 0;
	const retry = setInterval(() => {
		tries++;
		if (placeTrigger() || tries >= 30) clearInterval(retry); // 30 × 100ms = 3s cap
	}, 100);
}

export function initSizingDrawer() {
	const drawer = document.getElementById('sentiments-sizing-drawer');
	if (!drawer) return;

	// Move drawer to <body> so it's not trapped inside the sticky info column.
	if (drawer.parentElement !== document.body) {
		document.body.appendChild(drawer);
	}

	// Move the inline trigger to sit right under the size pills.
	portalSizeGuideTrigger();

	const panel    = drawer.querySelector('.sentiments-sizing-drawer__panel');
	let lastTrigger = null;

	const setState = (state) => {
		if (state === STATE_OPEN) {
			drawer.hidden = false;
			// Force a reflow so the transition runs.
			// eslint-disable-next-line no-unused-expressions
			drawer.offsetHeight;
			drawer.dataset.state = STATE_OPEN;
			document.body.classList.add('sentiments-drawer-open');
			panel?.focus({ preventScroll: true });
		} else {
			drawer.dataset.state = STATE_CLOSE;
			document.body.classList.remove('sentiments-drawer-open');
			// Wait for transition before hiding
			setTimeout(() => {
				if (drawer.dataset.state === STATE_CLOSE) drawer.hidden = true;
			}, 320);
			lastTrigger?.focus({ preventScroll: true });
		}
	};

	const open = (trigger) => {
		lastTrigger = trigger || document.activeElement;
		setState(STATE_OPEN);
	};

	const close = () => setState(STATE_CLOSE);

	// Trigger clicks (delegate from document so dynamically-rendered triggers work).
	document.addEventListener('click', (e) => {
		const trigger = e.target.closest('[data-sizing-drawer-trigger]');
		if (trigger) {
			e.preventDefault();
			open(trigger);
			return;
		}
		const closer = e.target.closest('[data-sizing-drawer-close]');
		if (closer && drawer.contains(closer)) {
			e.preventDefault();
			close();
		}
	});

	// ESC key + focus trap.
	drawer.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			close();
			return;
		}
		if (e.key !== 'Tab') return;

		const focusables = drawer.querySelectorAll(FOCUSABLE_SELECTOR);
		if (!focusables.length) return;
		const first = focusables[0];
		const last  = focusables[focusables.length - 1];

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault();
			first.focus();
		}
	});
}
